import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getWaitingConfirmServices,
    getPOCServices,
} from '@/modules/Service/services'
import { Service, ServiceStatus } from '@/modules/Service/types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Typogaphy } from 'vnetwork-ui'
import ButtonGroupCustomized from '@/components/ButtonGroupCustomized'
import { useState, useMemo } from 'react'
import { Clock, Package, FlaskConical, ArrowRight } from 'lucide-react'

type TabType = 'service' | 'poc'

const WaitingConfirmWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState<TabType>('service')

    // Fetch services waiting confirm
    const { data: servicesData, isLoading: isServicesLoading } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SERVICE, 'waiting-confirm-dashboard'],
            queryFn: () =>
                getWaitingConfirmServices({ page: 1, page_size: 10, q: '' }),
        })

    // Fetch POC services waiting confirm
    const { data: pocData, isLoading: isPocLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICE, 'poc-waiting-confirm-dashboard'],
        queryFn: () =>
            getPOCServices({
                page: 1,
                page_size: 10,
                q: '',
                filters: btoa(
                    JSON.stringify({
                        service_status: [ServiceStatus.WaitingConfirm],
                    })
                ),
            }),
    })

    const isLoading = isServicesLoading || isPocLoading

    const services = servicesData?.results || []
    const pocServices = pocData?.results || []

    const tabs = useMemo(
        () => [
            {
                key: 'service' as TabType,
                label: t('widgets.waiting_confirm_service', 'Dịch vụ'),
                count: servicesData?.pagination?.total || 0,
            },
            {
                key: 'poc' as TabType,
                label: 'POC',
                count: pocData?.pagination?.total || 0,
            },
        ],
        [
            services.length,
            pocServices.length,
            servicesData?.results?.length,
            pocData?.results?.length,
            t,
        ]
    )

    const currentList = activeTab === 'service' ? services : pocServices
    const currentTotal =
        activeTab === 'service'
            ? servicesData?.pagination?.total || 0
            : pocData?.pagination?.total || 0

    const handleServiceClick = (serviceId: number) => {
        navigate(
            generatePathDatacentral(paths.serviceDetail, { id: serviceId })
        )
    }

    const handleViewAll = () => {
        if (activeTab === 'service') {
            navigate(
                generatePathDatacentral(
                    paths.services,
                    {},
                    { tab: 'service-waiting-confirm' }
                )
            )
        } else {
            navigate(
                generatePathDatacentral(
                    paths.services,
                    {},
                    { tab: 'service-poc', service_status: 'waiting-confirm' }
                )
            )
        }
    }

    const renderServiceItem = (service: Service) => {
        const isPoc = activeTab === 'poc'

        return (
            <div
                key={service.service_id}
                onClick={() => handleServiceClick(service.service_id)}
                className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                        className={`p-1.5 rounded-full flex-shrink-0 ${isPoc ? 'bg-purple-100' : 'bg-orange-100'}`}
                    >
                        {isPoc ? (
                            <FlaskConical className="w-3.5 h-3.5 text-purple-600" />
                        ) : (
                            <Package className="w-3.5 h-3.5 text-orange-600" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs text-gray-900 truncate">
                            {service.service_name}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                            {service.customer_name}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                        {t('widgets.waiting', 'Chờ xác nhận')}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                </div>
            </div>
        )
    }

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Title */}
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <Typogaphy className="font-semibold text-primary">
                        {t('widgets.services.waiting_confirm')}
                    </Typogaphy>
                </div>

                {/* Tabs */}
                <div
                    className="flex-shrink-0 mb-3"
                    data-tour="waiting-confirm-tabs"
                >
                    <ButtonGroupCustomized
                        className="p-0.5"
                        active={activeTab}
                        onChange={(value) => setActiveTab(value as TabType)}
                        buttons={tabs.map((tab) => ({
                            label: tab.label,
                            value: tab.key,
                            count: tab.count,
                        }))}
                    />
                </div>

                {isLoading ? (
                    <div className="space-y-2 flex-1">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-14 rounded-lg" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Summary */}
                        <div
                            className="flex items-center gap-2 p-2.5 bg-orange-50 rounded-lg mb-3 flex-shrink-0"
                            data-tour={
                                activeTab === 'poc'
                                    ? 'waiting-poc-summary'
                                    : 'waiting-service-summary'
                            }
                        >
                            <Clock className="w-4 h-4 text-orange-600" />
                            <div>
                                <div className="text-lg font-bold text-orange-600">
                                    {currentTotal}
                                </div>
                                <div className="text-[10px] text-orange-600">
                                    {activeTab === 'service'
                                        ? t(
                                              'widgets.services_waiting',
                                              'Dịch vụ chờ xác nhận'
                                          )
                                        : t(
                                              'widgets.poc_waiting',
                                              'POC chờ xác nhận'
                                          )}
                                </div>
                            </div>
                        </div>

                        {/* List */}
                        <div className="space-y-1.5 flex-1">
                            {currentList.length === 0 ? (
                                <div className="text-center py-4 text-gray-500 text-sm">
                                    {t(
                                        'widgets.no_waiting',
                                        'Không có dịch vụ chờ xác nhận'
                                    )}
                                </div>
                            ) : (
                                <>
                                    {currentList
                                        .slice(0, 3)
                                        .map((service) =>
                                            renderServiceItem(service)
                                        )}
                                    {/* Empty placeholders */}
                                    {Array.from({
                                        length: Math.max(
                                            0,
                                            3 - currentList.length
                                        ),
                                    }).map((_, i) => (
                                        <div
                                            key={`empty-${i}`}
                                            className="h-[52px] bg-gray-50 rounded-lg border border-dashed border-gray-200"
                                        />
                                    ))}
                                </>
                            )}
                        </div>

                        {/* View All */}
                        {currentTotal > 0 && (
                            <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                                <button
                                    onClick={handleViewAll}
                                    className="text-xs text-primary hover:underline"
                                >
                                    {t('widgets.view_all')} ({currentTotal})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Paper>
    )
}

export default WaitingConfirmWidget
